import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_ANTISOCIAL_PERSONALITY_DISORDER,
  PERSONALITY_TEST_ASSESSMENT_ID,
  traitTitleKeyMap,
} from "../../utils/general-config";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import {
  getAntiSocialReportPageImage,
  getCookie,
  getEnneagramBannerImage,
  snakeToTitleCase,
} from "../../utils/utility-functions";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import ExpandableSection from "../../components/expandable-collapsible/component";

import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const AntisocialPersonalityDisorderReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_ANTISOCIAL_PERSONALITY_DISORDER[currentLocale] ||
            MODEL_TYPE_ANTISOCIAL_PERSONALITY_DISORDER[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const result1ImageText = "antisocial-results-1";
  const result2ImageText = "antisocial-results-2";
  const result1Image = getAntiSocialReportPageImage(result1ImageText);
  const result2Image = getAntiSocialReportPageImage(result2ImageText);
  const bannerImage = getEnneagramBannerImage();
  const sectionScores = contentData?.sectionScores;
  const understandingResultArray = contentData?.understandingResults?.Content;
  const understandingResult = contentData?.understandingResults;
  const sectionBreakdown = contentData?.sectionBreakdown;

  const resourcesContent = contentData?.resources;
  const nextStepsContent = contentData?.nextSteps;

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const progressBarColor = [
    "#ff6284",
    "#49A2E6",
    "#805bec",
    "#ffce55",
    "#4cc0c0",
    "#ec6af9",
  ];

  const colors = [
    "#eef4fe",
    "#f8f4fe",
    "#fdf6eb",
    "#ebfbeb",
    "#feeded",
    "#c7edf9",
    "#e7feeb",
    "#eff4fd",
    "#eae2fd",
  ];

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />
        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            {/* Title & Button Wrapper */}
            <div className="flex flex-col  md:flex-row items-start justify-between gap-4 pr-10 md:pr-16">
              {/* Title */}
              <h2
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-4xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CallToActionSection = ({ onClick1, onClick2 }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.testMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.discoverMessage")}
            </p>
            <TakeTestButton onClick={onClick1} />
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.bigFiveMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.strengthMesssge")}
            </p>
            <TakeTestButton onClick={onClick2} />
          </div>
        </div>
      </section>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
        style={{ filter: "drop-shadow(0 0 5px rgba(0,0,0,0.3))" }}
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          stroke="none"
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  const ProgressBar = ({ label, percentage, color }) => {
    return (
      <li>
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-700">{label}</span>
          <span className="text-sm" style={{ color }}>
            {percentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-5">
          <div
            className="h-5 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          ></div>
        </div>
      </li>
    );
  };

  const InfoListCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2 mt-4 pl-6 md:pl-10">
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const ContentCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    level,
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                  {heading + ": "}{" "}
                  <span className="text-[#805bec]">{level}</span>
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2 mt-4">
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const ProgressCircle = ({
    color = "#805aed",
    duration = 2000,
    targetPercentage = 50,
  }) => {
    const [percentage, setPercentage] = useState(0);
    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const intervalTime = 30; // Time in milliseconds for each update

    useEffect(() => {
      const steps = duration / intervalTime; // Number of updates
      const stepSize = targetPercentage / steps; // Percentage to increase per step

      let currentPercentage = 0;
      const interval = setInterval(() => {
        currentPercentage += stepSize;
        if (currentPercentage >= targetPercentage) {
          currentPercentage = targetPercentage;
          clearInterval(interval);
        }
        setPercentage(parseInt(currentPercentage)); // Ensure whole numbers
      }, intervalTime);

      return () => clearInterval(interval); // Cleanup interval on unmount
    }, [duration, targetPercentage]);

    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="w-full max-w-xs p-4 mx-auto">
        <div className="relative pb-[100%] ease-in duration-100">
          <svg
            className="absolute top-0 left-0 w-full h-full transform -rotate-90"
            viewBox="0 0 120 120"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke="#E5E7EB"
              strokeWidth="10"
              fill="none"
            />
            {/* Animated Progress Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke={color}
              strokeWidth="10"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{
                transition: "stroke-dashoffset 0.1s ease-in-out",
              }}
            />
          </svg>

          {/* Percentage Text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-2xl font-bold text-gray-700">
            <span>{targetPercentage}%</span>
          </div>
        </div>
      </div>
    );
  };

  const NextStepsCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-4 mt-4 pl-4 md:pl-6">
            {contentArray.map((item, index) => (
              <li key={index} className="flex flex-col gap-2">
                {/* Heading */}
                <h2 className="text-lg font-semibold text-gray-700">{`${
                  index + 1
                }. ${item.Heading}`}</h2>

                {/* Content with Bullet */}
                <div className="flex gap-4 items-start">
                  {/* Bullet Point */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Content Text */}
                  <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                    {item.Content}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const ResourcesCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className="p-4 md:p-6 rounded-2xl shadow-lg flex flex-col h-full overflow-hidden"
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div className="w-full">
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center break-all">
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 ${
              contentArray ? "mb-4" : ""
            } break-all`}
          >
            {overview}
          </p>
        )}

        {contentArray && (
          <ul className="space-y-4 mt-4 md:pl-6 pl-4 w-full">
            {contentArray.map((item, index) => (
              <li key={index} className="flex flex-col gap-2 w-full">
                {/* Root Heading */}
                <h2 className="text-lg font-semibold text-gray-700 break-all">
                  {`${index + 1}. ${item.RootHeading}`}
                </h2>

                {/* Content List */}
                <ul className="space-y-4 w-full">
                  {item.Content.map((contentItem, contentIndex) => (
                    <li
                      key={contentIndex}
                      className="flex gap-4 items-start w-full"
                    >
                      {/* Bullet Point */}
                      <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-1">
                        <CircleIcon character={""} size={16} />
                      </div>

                      {/* Content */}
                      <div className="flex flex-col w-full overflow-hidden">
                        {typeof contentItem === "object" &&
                        contentItem.Heading ? (
                          <>
                            <span className="font-bold text-gray-700 break-all">
                              {contentItem.Heading}
                            </span>
                            {contentItem.Content ? (
                              <a
                                href={contentItem.Content}
                                className="text-[#805bec] underline text-base md:text-lg leading-relaxed break-all overflow-hidden text-ellipsis"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  wordBreak: "break-all",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {contentItem.Content}
                              </a>
                            ) : null}
                          </>
                        ) : (
                          <a
                            href={contentItem}
                            className="text-[#805bec] underline text-base md:text-lg leading-relaxed break-all overflow-hidden text-ellipsis"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              wordBreak: "break-all",
                              overflowWrap: "break-word",
                            }}
                          >
                            {contentItem}
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  function getLevel(score) {
    if (1 <= score && score <= 2)
      return {
        englishCode: "Low",
        localizedCode: t("report.narcissistic_personality.score_level.low"),
      };
    if (2 < score && score <= 3)
      return {
        englishCode: "Moderate",
        localizedCode: t(
          "report.narcissistic_personality.score_level.moderate"
        ),
      };
    if (3 < score && score <= 4)
      return {
        englishCode: "High",
        localizedCode: t("report.narcissistic_personality.score_level.high"),
      };
    if (4 < score && score <= 5)
      return {
        englishCode: "Very High",
        localizedCode: t(
          "report.narcissistic_personality.score_level.very_high"
        ),
      };
    return null;
  }

  function getUpdatedOverallResult(score) {
    const level = getLevel(score);
    if (!level) return null;

    const result = understandingResultArray?.find((item) =>
      item.EnglishCode.toLowerCase().includes(level?.englishCode.toLowerCase())
    );
    return result || null;
  }

  function transformUserTraitsData(data) {
    if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

    const result = data.reduce((acc, item) => {
      if (
        item?.trait?.title &&
        typeof item.percentage === "number" &&
        typeof item.score === "number"
      ) {
        acc[item.trait.title] = {
          percentage: item.percentage,
          score: item.score,
          updatedScore: item.score * 10,
        };
      }
      return acc;
    }, {});

    const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

    return {
      result,
      totalScore,
      updatedTotalScore: totalScore * 10,
      avgTotalScore: totalScore / data.length,
    };
  }

  const updatedUserTraits = transformUserTraitsData(userTraits);
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  const avgTotalScoreLevel = getLevel(
    updatedUserTraits?.avgTotalScore
  )?.localizedCode;

  function updateSectionScores(sectionScores, updatedUserTraits) {
    if (!sectionScores || !updatedUserTraits || !sectionScores.Content) {
      return null;
    }

    const traitMapping = {
      "Impulsivity and Risk-Taking": "impulsivity_and_risk_taking",
      "Lack of Empathy": "lack_of_empathy",
      "Deceitfulness and Manipulation": "deceitfulness_and_manipulation",
      "Disregard for Social Norms and Laws":
        "disregard_for_social_norms_and_laws",
      "Aggression and Hostility": "aggression_and_hostility",
    };

    const updatedContent = sectionScores.Content.map((section) => {
      if (!section || !section.Heading) {
        return section;
      }

      const traitKey = traitMapping[section.Heading];
      const traitData = updatedUserTraits[traitKey];

      if (traitData && typeof traitData === "object") {
        return {
          Heading: section.Heading,
          percentage: traitData.percentage ?? null,
          updatedScore: traitData.updatedScore ?? null,
          score: traitData.score ?? null,
        };
      }
      return section;
    });

    return {
      ...sectionScores,
      Content: updatedContent,
    };
  }

  function getFilteredSectionBreakdown(
    updatedUserTraitsResult,
    sectionBreakdownArray
  ) {
    if (
      !updatedUserTraitsResult ||
      typeof updatedUserTraitsResult !== "object"
    ) {
      return [];
    }

    return sectionBreakdownArray
      ?.map((section) => {
        if (!section || !section.Heading || !section.Content) return null;

        const traitKey = section.EnglishCode.split(":")[0]
          .toLowerCase()
          .replace(/ /g, "_")
          .replace(/-/g, "_");

        const traitData = updatedUserTraitsResult[traitKey];

        if (!traitData || typeof traitData.score !== "number") return null;

        const level = getLevel(traitData.score);
        if (!level) return null;

        const levelContent = section.Content.find(
          (item) => item.EnglishCode === level.englishCode
        );
        return levelContent
          ? {
              Heading: section.Heading,
              Content: levelContent.Content,
              Level: level.localizedCode,
            }
          : null;
      })
      ?.filter((item) => item !== null);
  }

  function transformResources(resources) {
    if (
      !resources ||
      typeof resources !== "object" ||
      !Array.isArray(resources.Content)
    )
      return null;

    return resources.Content.map((section) => {
      if (
        !section ||
        typeof section !== "object" ||
        !section.Heading ||
        !Array.isArray(section.Content)
      )
        return null;

      const transformedContent = section.Content.map((item) => {
        if (typeof item === "string" && item.includes(":")) {
          const [heading, ...content] = item.split(":");
          return {
            Heading: heading.trim() + ":",
            Content: content.join(":").trim(),
          };
        }
        return item;
      }).filter((item) => item !== null && item !== undefined); // Remove null/undefined values

      return {
        RootHeading: section.Heading,
        Content: transformedContent,
      };
    }).filter((item) => item !== null && item !== undefined); // Remove null/undefined sections
  }

  const updatedSectionScores = updateSectionScores(
    sectionScores,
    updatedUserTraits?.result
  );

  const updatedOverallResult = getUpdatedOverallResult(
    updatedUserTraits?.avgTotalScore
  );

  const filteredSectionBreakdown = getFilteredSectionBreakdown(
    updatedUserTraits?.result,
    sectionBreakdown?.Content
  );

  const updatedResourcesContent = transformResources(resourcesContent);

  const progressScore = userTraits?.map((item, index) => {
    const localeKey =
      traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];
    return {
      label: snakeToTitleCase(item.trait[localeKey]),
      percentage: item?.percentage,
      color: progressBarColor[index % progressBarColor.length],
    };
  });

  // Function to replace score placeholder dynamically based on locale
  const getFormattedTotalScore = () => {
    if (!contentData?.totalScore) return "";

    // Get the placeholder key for the current locale
    const scorePlaceholder = "score"; // This will return "score" for EN and "puntaje" for ES

    // Replace the placeholder with the actual score
    return contentData.totalScore.replace(
      `{${scorePlaceholder}}`,
      updatedUserTraits?.updatedTotalScore || ""
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData?.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-2 flex justify-end gap-2 px-4 lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}

                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[30%_30%_40%] gap-4 items-center">
                      <div>
                        <h2 className="text-2xl lg:text-3xl font-semibold text-gray-700">
                          {getFormattedTotalScore()}/250
                        </h2>
                        <h2 className="text-xl lg:text-2xl font-semibold text-gray-700 my-4">
                          {updatedSectionScores?.RootHeading}
                        </h2>
                        <ul className="space-y-2">
                          {progressScore?.map((item, index) => (
                            <ProgressBar
                              key={index}
                              label={item?.label}
                              percentage={item?.percentage}
                              color={item?.color}
                            />
                          ))}
                        </ul>
                      </div>
                      <div className="flex justify-center">
                        <ProgressCircle
                          targetPercentage={parseFloat(
                            (
                              (updatedUserTraits?.updatedTotalScore / 250) *
                              100
                            ).toFixed(1)
                          )}
                        />
                      </div>
                      <div className="flex justify-center lg:justify-end pr-4">
                        <img
                          src={result1Image}
                          alt="Banner Image 1"
                          className="w-full h-auto object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={understandingResult?.RootHeading}
                      color="purple"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6 transition-all duration-300 ease-in-out">
                    <h2 className="text-2xl font-semibold text-gray-700 mt-6 mb-3">
                      {understandingResult?.FlavorText.split(":")[0] + ": "}
                      <span className="text-[#805bec]">
                        {avgTotalScoreLevel}
                      </span>
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-8 items-center">
                      <div>
                        <InfoListCard
                          character={"i"}
                          heading={updatedOverallResult?.Heading}
                          contentArray={updatedOverallResult?.Content}
                          bgClr={"#fffeed"}
                        />
                      </div>
                      <div className="flex items-cente justify-center">
                        <img
                          src={result2Image}
                          alt="Banner Image 1"
                          className="w-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={sectionBreakdown?.RootHeading}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed my-4">
                      {sectionBreakdown?.FlavorText}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                      {filteredSectionBreakdown?.map((item, index) => {
                        return (
                          <div key={index}>
                            <ContentCard
                              bgClr={colors[index % colors.length]}
                              heading={item?.Heading}
                              overview={item?.Content}
                              level={item?.Level}
                            />
                          </div>
                        );
                      })}
                      <InfoListCard
                        bgClr={colors[5]}
                        heading={contentData?.disclaimer?.RootHeading}
                        overview={contentData?.disclaimer?.Content}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t("report.narcissistic_personality.what_next")}
                      color="green"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                      <NextStepsCard
                        bgClr={colors[colors.length - 1]}
                        heading={nextStepsContent?.RootHeading}
                        contentArray={nextStepsContent?.Content}
                        character={"i"}
                      />
                      <ResourcesCard
                        bgClr={colors[colors.length - 1]}
                        heading={resourcesContent?.RootHeading}
                        contentArray={updatedResourcesContent}
                        character={"i"}
                      />
                    </div>

                    <p className={`text-lg text-gray-700 mt-8 mb-4`}>
                      {contentData?.conclusion}
                    </p>

                    <DescriptiveCard
                      heading={contentData?.note?.RootHeading}
                      overview={contentData?.note?.Content}
                      bgClr={"bg-yellow-50"}
                      textClr={"text-yellow-800"}
                    />
                  </div>
                </ExpandableSection>
              </section>

              <CallToActionSection
                onClick1={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
                onClick2={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
              />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AntisocialPersonalityDisorderReport;
